import { useState } from 'react';
import {
  ArrowLeftRight,
  ChevronRight,
  Smartphone,
  FileText,
  ShoppingCart,
  type LucideIcon,
} from 'lucide-react';

interface Txn {
  Icon: LucideIcon;
  color: string;
  name: string;
  date: string;
  amount: string;
  status: string;
  tone: 'green' | 'amber';
}

const TXNS: Txn[] = [
  { Icon: Smartphone, color: '#A892E8', name: 'Depósito Yape', date: '10 Jun, 2026', amount: '+ S/ 318.00', status: 'Completado', tone: 'green' },
  { Icon: FileText, color: '#8FB6EE', name: 'Cobro F001-0892', date: '10 Jun, 2026', amount: '+ S/ 1,242.00', status: 'Completado', tone: 'green' },
  { Icon: ShoppingCart, color: '#EBB04E', name: 'Pago Agro Andes', date: '09 Jun, 2026', amount: '− S/ 2,000.00', status: 'Pendiente', tone: 'amber' },
];

const SLIDES = [
  { t: 'Controla todas tus finanzas en un solo lugar', s: 'Facturación SUNAT, inventario, caja y cobranza — con datos claros para decidir mejor.' },
  { t: 'Emite comprobantes en segundos', s: 'Boletas y facturas electrónicas aceptadas por SUNAT, directo desde tu punto de venta.' },
  { t: 'Tu negocio, siempre al día', s: 'Reportes, alertas y flujo de caja en tiempo real para no perder ningún detalle.' },
];

/** Panel derecho (marketing) compartido por Login y Registro. */
export function AuthShowcase(): JSX.Element {
  const [tab, setTab] = useState<'in' | 'out'>('in');
  const [slide, setSlide] = useState(0);

  return (
    <div
      className="login-show"
      style={{
        position: 'relative',
        overflow: 'hidden',
        padding: 36,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundImage:
          'linear-gradient(160deg, #F2FBFF 0%, #E7F5FB 48%, #DDEFF8 100%), linear-gradient(rgba(120,180,210,.10) 1px, transparent 1px), linear-gradient(90deg, rgba(120,180,210,.10) 1px, transparent 1px)',
        backgroundSize: 'auto, 38px 38px, 38px 38px',
      }}
    >
      {/* floating transactions card */}
      <div className="card" style={{ padding: 16, boxShadow: '0 24px 60px -18px rgba(20,70,90,.28)', borderRadius: 18, marginBottom: 28 }}>
        <div className="row between aic" style={{ marginBottom: 14 }}>
          <div className="row aic" style={{ gap: 9 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--canvas)', display: 'grid', placeItems: 'center', color: 'var(--ink-2)' }}>
              <ArrowLeftRight size={16} />
            </div>
            <span className="fw7">Movimientos</span>
          </div>
          <button className="btn btn-secondary btn-sm" type="button">
            Ver todo
            <ChevronRight size={14} />
          </button>
        </div>
        <div className="seg" style={{ width: '100%', marginBottom: 12 }}>
          <button type="button" className={tab === 'in' ? 'on' : ''} style={{ flex: 1 }} onClick={() => setTab('in')}>
            Entradas
          </button>
          <button type="button" className={tab === 'out' ? 'on' : ''} style={{ flex: 1 }} onClick={() => setTab('out')}>
            Salidas
          </button>
        </div>
        <div className="col">
          {TXNS.map((t, i) => (
            <div key={t.name} className="row aic gap10" style={{ padding: '9px 2px', borderBottom: i < TXNS.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
              <div style={{ width: 34, height: 34, borderRadius: 9, display: 'grid', placeItems: 'center', background: `${t.color}26`, color: t.color, flex: 'none' }}>
                <t.Icon size={16} />
              </div>
              <div className="grow" style={{ minWidth: 0 }}>
                <div className="fw6 t-sm">{t.name}</div>
                <div className="t-xs muted">{t.date}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="tnum fw7 t-sm">{t.amount}</div>
                <span className={`badge badge-${t.tone}`} style={{ marginTop: 2 }}>
                  {t.status}
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="row center aic gap6 t-xs muted" style={{ marginTop: 10, padding: 8, background: 'var(--canvas)', borderRadius: 10 }}>
          Actualizado hace 5 min ·{' '}
          <a style={{ color: 'var(--ink)', fontWeight: 600, cursor: 'pointer' }}>Actualizar</a>
        </div>
      </div>

      <div style={{ textAlign: 'center', maxWidth: 380, margin: '0 auto' }}>
        <div className="t-h1 fw8" style={{ letterSpacing: '-.02em', marginBottom: 8 }}>
          {SLIDES[slide]?.t}
        </div>
        <p className="muted" style={{ margin: 0, lineHeight: 1.5 }}>
          {SLIDES[slide]?.s}
        </p>
      </div>
      <div className="row center gap6" style={{ marginTop: 22 }}>
        {SLIDES.map((s, i) => (
          <button
            key={s.t}
            type="button"
            onClick={() => setSlide(i)}
            aria-label={`Ir a la diapositiva ${i + 1}`}
            style={{
              width: i === slide ? 22 : 7,
              height: 7,
              borderRadius: 99,
              border: 'none',
              background: i === slide ? 'var(--ink)' : '#C3D3DC',
              transition: 'width .2s, background .2s',
              cursor: 'pointer',
            }}
          />
        ))}
      </div>
    </div>
  );
}
